<template>
  <div class="min-h-screen">
    <!-- Header -->
    <AppHeader />
    
    <!-- Hero Section -->
    <!-- Hero Section -->
    <section class="relative overflow-hidden bg-gray-50 pt-32 pb-20 lg:pt-40 lg:pb-28">
      <!-- Background Elements -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/30 blur-3xl"></div>
        <div class="absolute top-[20%] -right-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10">
        <div v-if="loadingHero" class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="space-y-6">
            <SkeletonLoader type="title" width="80%" />
            <SkeletonLoader type="text" width="100%" />
            <SkeletonLoader type="text" width="90%" />
            <div class="flex gap-4 mt-8">
              <SkeletonLoader type="button" />
              <SkeletonLoader type="button" />
            </div>
          </div>
          <div class="hidden lg:block">
            <SkeletonLoader type="card" height="500px" />
          </div>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <!-- Text Content -->
          <div class="max-w-2xl animate-slide-up">
            <div class="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 border border-primary-100 text-primary-600 text-sm font-medium mb-6">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2"></span>
              Innovating for the Future
            </div>
            
            <h1 class="font-heading text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {{ heroTitleParts.main }}
              <span class="text-gradient block mt-2">{{ heroTitleParts.highlight }}</span>
            </h1>
            
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              {{ heroSection?.data?.subtitle || 'We build innovative software solutions that drive business growth and digital transformation. Experience the power of modern technology.' }}
            </p>
            
            <div class="flex flex-wrap gap-4">
              <router-link to="/order-service" class="btn btn-primary text-lg px-8 py-4 shadow-xl shadow-primary-500/20 hover:shadow-primary-500/40">
                {{ heroSection?.data?.cta_primary || 'Get Started' }}
              </router-link>
              <router-link to="/portfolio" class="btn btn-outline text-lg px-8 py-4 border-2">
                {{ heroSection?.data?.cta_secondary || 'View Portfolio' }}
              </router-link>
            </div>

            <!-- Stats Row -->
            <div class="mt-12 grid grid-cols-3 gap-8 border-t border-gray-200 pt-8">
              <div v-for="(stat, index) in statistics.slice(0, 3)" :key="index">
                <div class="text-3xl font-bold text-gray-900 mb-1">{{ stat.value }}</div>
                <div class="text-sm text-gray-600 font-medium">{{ stat.label }}</div>
              </div>
            </div>
          </div>
          
          <!-- Hero Illustration -->
          <div class="relative lg:h-[600px] animate-fade-in animation-delay-200 hidden lg:block">
            <HeroIllustration />
          </div>
        </div>
      </div>
    </section>
    
    <!-- Services Preview -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div v-if="loadingServices" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="300px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="600px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SkeletonLoader v-for="i in 3" :key="i" type="card" />
          </div>
        </div>

        <div v-else>
          <div class="text-center mb-16">
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {{ servicesPreviewSection?.data?.title || 'Our Services' }}
            </h2>
            <p class="text-xl text-gray-600 max-w-3xl mx-auto">
              {{ servicesPreviewSection?.data?.description || 'We offer comprehensive software development services to help your business thrive in the digital world.' }}
            </p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              v-for="service in previewServices" 
              :key="service.id"
              class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white rounded-2xl border border-gray-100"
            >
              <!-- Banner Image -->
              <div class="relative h-48 bg-gray-100 overflow-hidden">
                <img 
                  v-if="service.image"
                  :src="getBannerUrl(service.image)" 
                  :alt="service.name"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-100">
                  <svg class="w-12 h-12 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <!-- Overlay -->
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <!-- Content -->
              <div class="p-6">
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">{{ service.name }}</h3>
                <p class="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">{{ service.short_description }}</p>
                <router-link :to="{ name: 'ServiceDetail', params: { id: service.uid } }" class="text-primary-600 font-medium hover:text-primary-700 inline-flex items-center group-hover:translate-x-1 transition-transform">
                  Learn More
                  <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
          
          <div class="text-center mt-12">
            <router-link to="/services" class="btn btn-primary">
              View All Services
            </router-link>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Top Products Section -->
    <section class="section-padding bg-gray-50 relative overflow-hidden">
      <!-- Decorative Background -->
      <div class="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
      <div class="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-96 h-96 bg-secondary-200/20 blur-3xl rounded-full"></div>

      <div class="container-custom relative z-10">
        <div v-if="loadingProducts" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="300px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="600px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SkeletonLoader v-for="i in 3" :key="i" type="card" height="400px" />
          </div>
        </div>

        <div v-else-if="topProducts.length > 0">
          <div class="text-center mb-16">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white border border-primary-100 text-primary-600 text-sm font-medium mb-4 shadow-sm">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
              Featured Products
            </div>
            <h2 class="font-heading text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Top Innovation
            </h2>
            <p class="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our best-performing software products and digital solutions that are transforming businesses worldwide.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="product in topProducts" 
              :key="product.id"
              class="group relative"
            >
              <!-- Card Background with Glassmorphism -->
              <div class="absolute inset-0 bg-gradient-to-br from-white/90 to-white/50 backdrop-blur-lg rounded-[1.5rem] border border-white/60 shadow-xl transition-all duration-500 group-hover:shadow-primary-500/15 group-hover:scale-[1.01]"></div>
              
              <!-- Content Wrapper -->
              <div class="relative p-5 h-full flex flex-col">
                <!-- Top Badge Row -->
                <div class="flex items-center justify-between mb-4">
                  <div :class="['px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase', getPlatformClass(product.platform)]">
                    {{ product.platform }}
                  </div>
                  <div class="text-base font-extrabold text-gray-900">
                    {{ product.price == 0 ? 'FREE' : '$' + product.price }}
                  </div>
                </div>

                <!-- Product Banner/Visual -->
                <div class="relative aspect-[16/9] mb-4 rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-500 bg-gray-50">
                  <img 
                    v-if="product.banner_image_url"
                    :src="product.banner_image_url" 
                    :alt="product.title"
                    class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
                    <svg class="w-12 h-12 text-primary-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <!-- Soft Overlay -->
                  <div class="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>

                <!-- Product Identity -->
                <div class="flex items-center gap-3 mb-4">
                  <div class="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-md border-2 border-white bg-white group-hover:rotate-2 transition-transform duration-500">
                    <img v-if="product.icon_image_url" :src="product.icon_image_url" :alt="product.title" class="w-full h-full object-cover">
                    <div v-else class="w-full h-full bg-primary-100 flex items-center justify-center text-primary-600 text-lg font-black">
                      {{ product.title?.charAt(0) }}
                    </div>
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-lg font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                      {{ product.title }}
                    </h3>
                    <div class="flex items-center gap-1.5">
                      <div class="flex text-yellow-400">
                        <svg v-for="i in 5" :key="i" class="w-3 h-3 fill-current" :class="i <= Math.round(product.rating || 0) ? 'text-yellow-400' : 'text-gray-200'" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      </div>
                      <span class="text-[10px] font-bold text-gray-400">({{ product.rating || '0.0' }})</span>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <p class="text-gray-600 text-xs leading-relaxed mb-6 line-clamp-2">
                  {{ product.short_description }}
                </p>

                <!-- Actions Container -->
                <div class="mt-auto pt-4 border-t border-gray-100/60 flex gap-2">
                  <router-link 
                    :to="{ name: 'ProductDetail', params: { id: product.uid } }"
                    class="flex-1 btn btn-primary flex items-center justify-center gap-1.5 py-2.5 rounded-xl shadow-md shadow-primary-500/10 hover:shadow-primary-500/20 transition-all text-sm font-bold"
                  >
                    View Project
                    <svg class="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </router-link>

                  <!-- Install Button Overlay for Mobile -->
                  <a 
                    v-if="product.platform === 'mobile' && product.download_url"
                    :href="product.download_url"
                    target="_blank"
                    class="flex-1 btn bg-gray-900 hover:bg-black text-white flex items-center justify-center gap-1.5 py-2.5 rounded-xl shadow-md transition-all text-sm font-bold"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Install App
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="text-center mt-12">
            <router-link to="/products" class="btn btn-outline border-2 px-10 py-3 group">
              View All Products
              <span class="inline-block transition-transform group-hover:translate-x-1 ml-2">→</span>
            </router-link>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Why Choose Us -->
    <section class="section-padding bg-gray-50">
      <div class="container-custom">
        <div v-if="loadingWhyUs" class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div class="space-y-6">
            <SkeletonLoader type="title" />
            <SkeletonLoader type="text" />
            <SkeletonLoader type="text" />
            <div class="space-y-4">
              <SkeletonLoader v-for="i in 4" :key="i" type="card" height="100px" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-6">
            <SkeletonLoader v-for="i in 4" :key="i" type="stat" />
          </div>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              {{ whyChooseUsSection?.data?.title || 'Why Choose Us?' }}
            </h2>
            <p class="text-xl text-gray-600 mb-8">
              {{ whyChooseUsSection?.data?.description || 'We combine expertise, innovation, and dedication to deliver exceptional results.' }}
            </p>
            <div class="space-y-6">
              <div 
                v-for="(feature, index) in whyChooseUsFeatures" 
                :key="index"
                class="flex items-start space-x-4"
              >
                <div class="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 mb-2">{{ feature.title }}</h3>
                  <p class="text-gray-600">{{ feature.description }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-6">
            <div class="space-y-6">
              <div 
                v-for="(stat, index) in statistics.slice(0, 2)" 
                :key="index"
                class="card p-6 text-center"
              >
                <div class="text-2xl font-bold text-primary-600 mb-2">{{ stat.value }}</div>
                <div class="text-gray-600 text-sm">{{ stat.label }}</div>
              </div>
            </div>
            <div class="space-y-6 mt-12">
              <div 
                v-for="(stat, index) in statistics.slice(2, 4)" 
                :key="index"
                class="card p-6 text-center"
              >
                <div class="text-2xl font-bold text-primary-600 mb-2">{{ stat.value }}</div>
                <div class="text-gray-600 text-sm">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="relative overflow-hidden bg-gray-50 section-padding">
      <!-- Background Elements matching Hero -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] rounded-full bg-purple-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom text-center relative z-10">
        <div v-if="loadingHero" class="space-y-6">
          <SkeletonLoader type="title" width="400px" class="mx-auto" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
          <div class="flex justify-center gap-4">
            <SkeletonLoader type="button" />
            <SkeletonLoader type="button" />
          </div>
        </div>

        <div v-else class="max-w-4xl mx-auto">
          <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
            <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
            Let's Build Something Amazing
          </div>
          
          <h2 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {{ ctaSection?.data?.title || 'Ready to Start Your Project?' }}
          </h2>
          <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            {{ ctaSection?.data?.description || 'Let\'s discuss how we can help transform your ideas into reality.' }}
          </p>
          <div class="flex flex-wrap justify-center gap-4">
            <router-link to="/order-service" class="btn btn-primary text-lg px-8 py-4 shadow-xl shadow-primary-500/20 hover:shadow-primary-500/40">
              {{ ctaSection?.data?.cta_primary || 'Get in Touch' }}
            </router-link>
            <router-link to="/portfolio" class="btn btn-outline text-lg px-8 py-4 border-2 bg-white/50 backdrop-blur-sm hover:bg-white">
              {{ ctaSection?.data?.cta_secondary || 'View Our Work' }}
            </router-link>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useContentStore } from '@/stores/content'
import { useStatisticsStore } from '@/stores/statistics'
import { useWhyChooseUsStore } from '@/stores/whyChooseUs'
import { useServiceStore } from '@/stores/services'
import { useProductStore } from '@/stores/products'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'
import HeroIllustration from '@/components/home/HeroIllustration.vue'

const contentStore = useContentStore()
const statisticsStore = useStatisticsStore()
const whyChooseUsStore = useWhyChooseUsStore()
const serviceStore = useServiceStore()
const productStore = useProductStore()

// Per-section loading states for progressive reveal
const loadingHero = ref(true)
const loadingServices = ref(true)
const loadingWhyUs = ref(true)
const loadingProducts = ref(true)

// Load content independently so each section resolves as soon as its data arrives
onMounted(() => {
  // Hero content
  contentStore.fetchPageSections('home')
    .finally(() => { loadingHero.value = false })

  // Services preview
  serviceStore.fetchServices()
    .finally(() => { loadingServices.value = false })

  // Why Choose Us
  Promise.all([
    whyChooseUsStore.fetchFeatures(),
    statisticsStore.fetchStatistics()
  ]).finally(() => { loadingWhyUs.value = false })

  // Top Products
  productStore.fetchTopProducts()
    .finally(() => { loadingProducts.value = false })
})

// Computed properties for sections
const homeSections = computed(() => contentStore.pageSections.home || [])
const heroSection = computed(() => homeSections.value.find(s => s.section === 'hero'))
const servicesPreviewSection = computed(() => homeSections.value.find(s => s.section === 'services_preview'))
const whyChooseUsSection = computed(() => homeSections.value.find(s => s.section === 'why_choose_us'))
const ctaSection = computed(() => homeSections.value.find(s => s.section === 'cta'))

const heroTitleParts = computed(() => {
  const title = heroSection.value?.data?.title || 'Transform Your Ideas into Digital Reality'
  const words = title.split(' ')
  if (words.length === 0) return { main: '', highlight: '' }
  
  const highlight = words.pop()
  const main = words.join(' ')
  
  return { main, highlight }
})

const statistics = computed(() => {
  const stats = statisticsStore.statistics
  if (stats && stats.length > 0) {
    return stats
  }
  // Fallback data
  return [
    { value: '200+', label: 'Projects Completed' },
    { value: '100+', label: 'Happy Clients' },
    { value: '50+', label: 'Team Members' },
    { value: '7+', label: 'Years Experience' }
  ]
})

// Get preview services from API
const previewServices = computed(() => {
  const services = serviceStore.getActiveServices()
  return services.slice(0, 3) // Show first 3 services
})

const topProducts = computed(() => {
  return productStore.topProducts.slice(0, 3)
})

// Helper function to get banner URL
const getBannerUrl = (imagePath) => {
  return serviceStore.getBannerUrl(imagePath)
}

const whyChooseUsFeatures = computed(() => {
  const features = whyChooseUsStore.activeFeatures
  if (features && features.length > 0) {
    return features
  }
  // Fallback data only if API fails
  return [
    {
      title: 'Expert Team',
      description: 'Our skilled developers and designers have years of experience in cutting-edge technologies.'
    },
    {
      title: 'Agile Process',
      description: 'We follow agile methodologies to ensure fast delivery and continuous improvement.'
    },
    {
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality control processes ensure bug-free, reliable solutions.'
    },
    {
      title: 'Ongoing Support',
      description: '24/7 support and maintenance to keep your applications running smoothly.'
    }
  ]
})
const getPlatformClass = (platform) => {
  const classes = {
    'mobile': 'bg-indigo-100 text-indigo-700',
    'web': 'bg-emerald-100 text-emerald-700',
    'desktop': 'bg-amber-100 text-amber-700',
    'cross-platform': 'bg-rose-100 text-rose-700'
  }
  return classes[platform] || 'bg-gray-100 text-gray-700'
}
</script>


